import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import type { PreparationForm, CreatePreparationFormInput } from '../types';
import { toast } from '@/shared/lib/toast';

/**
 * Hook para buscar formulários de preparação
 */
export function usePreparationForms(employeeId?: string) {
  return useQuery({
    queryKey: ['feedback-pdi', 'preparation-forms', employeeId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (employeeId) params.append('employeeId', employeeId);

      const response = await api.get<{ forms: PreparationForm[]; total: number }>(
        `/feedback-pdi/preparation-forms?${params.toString()}`
      );

      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Hook para criar formulário de preparação
 */
export function useCreatePreparationForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePreparationFormInput) => {
      const response = await api.post<{ form: PreparationForm }>(
        '/feedback-pdi/preparation-forms',
        data
      );
      return response.data.form;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'preparation-forms'] });
      toast.success('Formulário enviado com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao enviar formulário');
    },
  });
}

/**
 * Hook para atualizar formulário de preparação
 */
export function useUpdatePreparationForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreatePreparationFormInput>;
    }) => {
      const response = await api.put<{ form: PreparationForm }>(
        `/feedback-pdi/preparation-forms/${id}`,
        data
      );
      return response.data.form;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'preparation-forms'] });
      toast.success('Formulário atualizado!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao atualizar formulário');
    },
  });
}
